import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  ArrowPathIcon,
  ChevronRightIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
  FolderOpenIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  ShareIcon,
  TableCellsIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import OverflowMenu from "../../components/ui/OverflowMenu";
import { HelpTitle } from "../../components/Help/HelpButton";
import FoldersGuide from "../../components/Help/guides/FoldersGuide";
import FolderNameDialog, { examSavedMessage } from "../../components/QuestionFolders/FolderNameDialog";
import DeleteFolderDialog from "../../components/QuestionFolders/DeleteFolderDialog";
import AddSubjectDialog from "../../components/QuestionFolders/AddSubjectDialog";
import ShareFolderDialog from "../../components/QuestionFolders/ShareFolderDialog";
import SubjectSettingsDialog from "../../components/QuestionFolders/SubjectSettingsDialog";
import { getQuestionFolders, getQuestions, getSubjects } from "../../services/questionService";
import { UserContext } from "../../utils/contexts/UserContext";
import { canBuildVariants, canShareFolder, isAdminUser } from "../../utils/folders";
import { canOpenPage } from "../../utils/permissions";
import { isCanceledError } from "../../utils/examError";
import { focusRing, Spinner } from "../Exams/TestBuilder/ui";
import TestFromFolderDialog from "./TestFromFolderDialog";

const bankHref = (uuid, tab) => `/questions?folder=${uuid}${tab ? `&tab=${tab}` : ""}`;
const streamTag = { kk: "ҚАЗ", ru: "РУС" };

// A folder as macOS draws it: a darker back with its tab, a lighter front. `muted` for one with nothing inside.
function FolderGlyph({ muted = false, className = "" }) {
  const back = muted ? "#cbd5e1" : "#4f9fe8";
  const front = muted ? "#e2e8f0" : "#7cbcf4";
  return (
    <svg viewBox="0 0 96 76" className={className} aria-hidden="true">
      <path d="M6 10a6 6 0 0 1 6-6h22.6a6 6 0 0 1 4.6 2.1L44 12h40a6 6 0 0 1 6 6v6H6V10Z" fill={back} />
      <rect x="6" y="16" width="84" height="56" rx="6" fill={back} />
      <rect x="6" y="22" width="84" height="50" rx="6" fill={front} />
      <rect x="6" y="22" width="84" height="4" rx="2" fill="#ffffff" opacity="0.35" />
    </svg>
  );
}

// One folder: opens on a click; its actions in "⋯" (shown on hover or focus from md, always on phones).
function FolderTile({ to, name, count, tag, menu, menuLabel }) {
  const [t] = useTranslation();
  return (
    <li className="group relative">
      <Link
        to={to}
        className={`flex h-full flex-col items-center gap-1.5 rounded-2xl px-2 pb-3 pt-4 text-center transition duration-200 hover:bg-white hover:shadow-sm hover:ring-1 hover:ring-slate-200 ${focusRing}`}
      >
        <span className="relative">
          <FolderGlyph muted={!count} className="h-20 w-24 drop-shadow-sm transition duration-200 group-hover:-translate-y-0.5 motion-reduce:transform-none" />
          {tag && (
            <span className="absolute bottom-2 right-1 rounded bg-white/90 px-1 text-[10px] font-semibold tracking-wide text-slate-700 shadow-sm">{tag}</span>
          )}
        </span>
        <span className="line-clamp-2 text-sm font-medium text-slate-900 [overflow-wrap:anywhere]">{name}</span>
        <span className="text-xs tabular-nums text-slate-500">{t("folders_page.questions", { count: count || 0 })}</span>
      </Link>
      {menu.length > 0 && (
        <div className="absolute right-1 top-1 md:opacity-0 md:transition md:focus-within:opacity-100 md:group-hover:opacity-100">
          {/* Drawn over the page and kept on screen: a tile at the edge of the grid would cut it off. */}
          <OverflowMenu label={menuLabel} items={menu} portal />
        </div>
      )}
    </li>
  );
}

/**
 * The folders as a Finder window (/folders, /folders/:uuid): the exam folders as folder icons, an exam folder's
 * subjects inside it; a subject opens its questions in the bank. Each folder's actions sit in its "⋯" (open, the
 * matrix, a test from its approved questions, share, settings, delete), as the bank allows them to the user.
 */
export default function Folders() {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const { user } = useContext(UserContext) || {};
  const isAdmin = isAdminUser(user);
  const makesTests = canOpenPage(user, "/test_list");
  const [folders, setFolders] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [noFolderCount, setNoFolderCount] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [search, setSearch] = useState("");
  const [nameDialog, setNameDialog] = useState({ open: false, folder: null });
  const [addSubject, setAddSubject] = useState({ open: false, exam: null });
  const [settings, setSettings] = useState({ open: false, folder: null });
  const [deleting, setDeleting] = useState({ open: false, folder: null });
  const [sharing, setSharing] = useState({ open: false, folder: null });
  const [testFrom, setTestFrom] = useState({ open: false, folder: null });

  const load = useCallback((signal) => {
    setLoadFailed(false);
    getQuestionFolders({ signal })
      .then((list) => setFolders(Array.isArray(list) ? list : list?.results || []))
      .catch((err) => {
        if (!isCanceledError(err)) setLoadFailed(true);
      });
    getQuestions({ params: { page: 1, page_size: 1, folder: "null" }, signal })
      .then((res) => setNoFolderCount(typeof res?.count === "number" ? res.count : null))
      .catch(() => {});
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);
  // The subject catalog, for adding a subject (admins).
  useEffect(() => {
    if (!isAdmin) return undefined;
    const controller = new AbortController();
    getSubjects({ params: { page: 1, page_size: 100 }, signal: controller.signal })
      .then((res) => setSubjects(Array.isArray(res) ? res : res?.results || []))
      .catch(() => {});
    return () => controller.abort();
  }, [isAdmin]);
  useEffect(() => setSearch(""), [uuid]);

  const byUuid = useMemo(() => new Map((folders || []).map((f) => [f.uuid, f])), [folders]);
  const exam = uuid ? byUuid.get(uuid) || null : null;
  const examOf = (f) => (f?.parent ? byUuid.get(f.parent) : f) || null;
  const query = search.trim().toLowerCase();
  const matches = (f) => !query || f.name.toLowerCase().includes(query);
  const exams = (folders || []).filter((f) => !f.parent);
  const shown = (exam ? (folders || []).filter((f) => f.parent === exam.uuid) : exams).filter(matches);
  const subjectsOf = (e) => (folders || []).filter((f) => f.parent === e.uuid);

  const reload = () => load();
  const examMenu = (e) => [
    { key: "open", label: t("folders_page.open"), Icon: FolderOpenIcon, to: `/folders/${e.uuid}` },
    { key: "bank", label: t("folders_page.open_bank"), Icon: ClipboardDocumentListIcon, to: bankHref(e.uuid) },
    isAdmin && { key: "add", label: t("folders.add_subject"), Icon: PlusIcon, onClick: () => setAddSubject({ open: true, exam: e }) },
    isAdmin && { key: "settings", label: t("folders_page.settings"), Icon: Cog6ToothIcon, onClick: () => setNameDialog({ open: true, folder: e }) },
    isAdmin && { key: "delete", label: t("folders.delete_exam"), Icon: TrashIcon, danger: true, onClick: () => setDeleting({ open: true, folder: e }) },
  ];
  const subjectMenu = (s) => [
    { key: "open", label: t("folders_page.open_bank"), Icon: ClipboardDocumentListIcon, to: bankHref(s.uuid) },
    { key: "matrix", label: t("folders_page.matrix"), Icon: TableCellsIcon, to: bankHref(s.uuid, "matrix") },
    canBuildVariants(s.role) && { key: "variants", label: t("folders_page.variants"), Icon: DocumentDuplicateIcon, to: bankHref(s.uuid, "variant") },
    makesTests && { key: "test", label: t("folders_page.make_test"), Icon: PlusIcon, onClick: () => setTestFrom({ open: true, folder: s }) },
    canShareFolder(s.role) && { key: "share", label: t("folders.share.button"), Icon: ShareIcon, onClick: () => setSharing({ open: true, folder: s }) },
    isAdmin && { key: "settings", label: t("folders_page.settings"), Icon: Cog6ToothIcon, onClick: () => setSettings({ open: true, folder: s }) },
    isAdmin && { key: "delete", label: t("folders.delete_subject"), Icon: TrashIcon, danger: true, onClick: () => setDeleting({ open: true, folder: s }) },
  ];

  let body;
  if (!folders && !loadFailed) {
    body = (
      <div className="flex items-center justify-center gap-3 py-20 text-sm text-slate-500" role="status">
        <Spinner className="h-5 w-5" />
        {t("folders_page.loading")}
      </div>
    );
  } else if (!folders) {
    body = (
      <div role="alert" className="flex flex-col items-center gap-3 py-20 text-sm text-slate-600">
        {t("folders_page.load_failed")}
        <button type="button" onClick={reload} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 font-medium text-blue-700 hover:bg-blue-50 ${focusRing}`}>
          <ArrowPathIcon className="h-4 w-4" aria-hidden="true" />
          {t("folders_page.retry")}
        </button>
      </div>
    );
  } else if (uuid && !exam) {
    body = <p className="py-20 text-center text-sm text-slate-500">{t("folders_page.not_found")}</p>;
  } else {
    const tiles = shown.map((f) =>
      exam ? (
        <FolderTile
          key={f.uuid}
          to={bankHref(f.uuid)}
          name={f.name}
          count={f.questions_count}
          tag={streamTag[f.stream]}
          menu={subjectMenu(f).filter(Boolean)}
          menuLabel={t("folders_page.actions_of", { name: f.name })}
        />
      ) : (
        <FolderTile
          key={f.uuid}
          to={`/folders/${f.uuid}`}
          name={f.name}
          count={f.questions_count}
          menu={examMenu(f).filter(Boolean)}
          menuLabel={t("folders_page.actions_of", { name: f.name })}
        />
      )
    );
    body =
      tiles.length || (!exam && !query && noFolderCount) ? (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8">
          {tiles}
          {/* The questions outside every folder, as one more folder at the end of the top level. */}
          {!exam && !query && noFolderCount > 0 && (
            <FolderTile to={bankHref("null")} name={t("folders.no_folder")} count={noFolderCount} menu={[]} menuLabel="" />
          )}
        </ul>
      ) : (
        <p className="py-16 text-center text-sm text-slate-500">
          {query ? t("folders_page.no_match", { search: search.trim() }) : exam ? t("folders_page.empty_exam") : t("folders_page.empty")}
        </p>
      );
  }

  const totalQuestions = exam ? exam.questions_count || 0 : null;

  return (
    <div className="flex min-w-0 flex-col gap-5 motion-safe:animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          {exam && (
            <nav aria-label={t("folders_page.path")} className="mb-1 flex items-center gap-1 text-sm text-slate-500">
              <Link to="/folders" className={`rounded px-1 hover:text-slate-900 hover:underline ${focusRing}`}>
                {t("nav.folders")}
              </Link>
              <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
              <span className="truncate text-slate-700" aria-current="page">
                {exam.name}
              </span>
            </nav>
          )}
          {/* The guide's "?" right after the title. */}
          <HelpTitle
            as="h1"
            helpId="folders"
            helpTitle={t("help.folders.title")}
            guide={<FoldersGuide makesTests={makesTests} noFolder={noFolderCount > 0} />}
            className="[overflow-wrap:anywhere]"
          >
            {exam ? exam.name : t("nav.folders")}
          </HelpTitle>
          <p className="mt-1 text-sm text-slate-500">
            {exam
              ? t("folders_page.exam_summary", { subjects: t("folders_page.subjects", { count: subjectsOf(exam).length }), questions: t("folders_page.questions", { count: totalQuestions }) })
              : t("folders_page.subtitle")}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
          <div className="relative min-w-0 flex-1 sm:w-64 sm:flex-none">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("folders_page.search")}
              aria-label={t("folders_page.search")}
              className="h-10 w-full rounded-lg border-slate-300 pl-9 text-sm shadow-sm placeholder:text-slate-500 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 max-sm:h-11"
            />
          </div>
          {isAdmin &&
            (exam ? (
              <button
                type="button"
                onClick={() => setAddSubject({ open: true, exam })}
                className={`inline-flex h-10 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-blue-700 max-sm:h-11 ${focusRing}`}
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                {t("folders.add_subject")}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setNameDialog({ open: true, folder: null })}
                className={`inline-flex h-10 items-center gap-1.5 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm transition duration-200 hover:bg-blue-700 max-sm:h-11 ${focusRing}`}
              >
                <PlusIcon className="h-4 w-4" aria-hidden="true" />
                {t("folders.new_exam")}
              </button>
            ))}
          {exam && <OverflowMenu label={t("folders_page.actions_of", { name: exam.name })} items={examMenu(exam).filter((item) => item && !["open", "add"].includes(item.key))} />}
        </div>
      </header>

      <section aria-label={exam ? t("folders_page.subjects_of", { name: exam.name }) : t("nav.folders")}>{body}</section>

      {isAdmin && (
        <>
          <FolderNameDialog
            isOpen={nameDialog.open}
            folder={nameDialog.folder}
            onClose={() => setNameDialog((d) => ({ ...d, open: false }))}
            onSaved={(saved) => {
              const before = nameDialog.folder;
              setNameDialog({ open: false, folder: before });
              toast.success(examSavedMessage(t, before, saved));
              reload();
            }}
          />
          <AddSubjectDialog
            isOpen={addSubject.open}
            exam={addSubject.exam}
            subjects={subjects}
            onClose={() => setAddSubject((d) => ({ ...d, open: false }))}
            onAdded={() => {
              setAddSubject((d) => ({ ...d, open: false }));
              toast.success(t("folders.toast.subject_added"));
              reload();
            }}
          />
          <SubjectSettingsDialog
            isOpen={settings.open}
            folder={settings.folder}
            examName={examOf(settings.folder)?.name || ""}
            onClose={() => setSettings((d) => ({ ...d, open: false }))}
            onSaved={() => {
              setSettings((d) => ({ ...d, open: false }));
              toast.success(t("folders.toast.subject_saved"));
              reload();
            }}
          />
          <DeleteFolderDialog
            isOpen={deleting.open}
            folder={deleting.folder}
            examName={examOf(deleting.folder)?.name || ""}
            onClose={() => setDeleting((d) => ({ ...d, open: false }))}
            onDeleted={(deleted) => {
              setDeleting({ open: false, folder: deleted });
              toast.success(deleted.parent ? t("folders.toast.subject_deleted") : t("folders.toast.exam_deleted"));
              if (!deleted.parent && deleted.uuid === uuid) navigate("/folders");
              reload();
            }}
          />
        </>
      )}
      <ShareFolderDialog
        isOpen={sharing.open}
        folder={sharing.folder}
        examName={examOf(sharing.folder)?.name || ""}
        onClose={() => setSharing((d) => ({ ...d, open: false }))}
      />
      {makesTests && (
        <TestFromFolderDialog
          isOpen={testFrom.open}
          folder={testFrom.folder}
          examName={examOf(testFrom.folder)?.name || ""}
          onClose={() => setTestFrom((d) => ({ ...d, open: false }))}
        />
      )}
    </div>
  );
}
